// The shell: one codebase, two presentations. CSS decides the layout; this file only routes and renders.

import { html } from './html.ts';
import { t } from './i18n.ts';
import { acts, forms, go, local, path, resetLocals } from './core.ts';
import type { Screen, Tab } from './core.ts';
import { DAY, world as w } from './world.ts';
import { seed } from './seed.ts';
import { isMember, stewards } from './queries.ts';
import { explain } from './explain.ts';
import { icon, whyCard } from './ui.ts';
import { jump, renderRecord, setAudit, setRecordMode } from './record.ts';
import { SEAT_ORDER } from './cast.ts';
import { fmtDateTime } from './format.ts';
import { chatScreen, chatsScreen, feedScreen, itemScreen, sellScreen } from './screens/market.ts';
import { appealFormScreen, languagePicker, meScreen, personScreen, rateScreen, replyScreen, tradeScreen } from './screens/trade.ts';
import { appealCaseScreen, caseScreen, casesScreen, decideAppealScreen, decideScreen, reportScreen, rulesScreen, triageScreen, vouchScreen } from './screens/govern.ts';
import { codeScreen, joinedScreen, phoneScreen, readyScreen, vouchWaitScreen, welcomeScreen } from './screens/join.ts';

export const DESKTOP = '(min-width: 1024px) and (pointer: fine) and (hover: hover)';
const desktop = matchMedia(DESKTOP);

const $ = <T extends HTMLElement>(id: string): T => document.getElementById(id) as T;

function route(): Screen {
  const me = w.me;
  const [a, b = ''] = path();
  if (me.role === 'community') return rulesScreen();
  if (me.role === 'triage') return a === 'rules' ? rulesScreen() : triageScreen();
  if (!isMember(w, me.id)) {
    if (a === 'join' && b === 'phone') return phoneScreen();
    if (a === 'join' && b === 'code') return codeScreen();
    if (a === 'join' && b === 'ready') return readyScreen();
    if (a === 'join' && b === 'vouch') return vouchWaitScreen();
    return welcomeScreen();
  }
  const steward = stewards(w).includes(me.id);
  switch (a) {
    case 'joined':
      return joinedScreen();
    case 'item':
      return itemScreen(b);
    case 'sell':
      return sellScreen();
    case 'chats':
      return chatsScreen();
    case 'chat':
      return chatScreen('chat', b);
    case 'ask':
      return chatScreen('ask', b);
    case 'trade':
      return tradeScreen(b);
    case 'rate':
      return rateScreen(b);
    case 'reply':
      return replyScreen(b);
    case 'appeal-form':
      return appealFormScreen(b);
    case 'appeal':
      return steward ? appealCaseScreen(b) : feedScreen();
    case 'decide-appeal':
      return steward ? decideAppealScreen(b) : feedScreen();
    case 'person':
      return personScreen(b);
    case 'me':
      return meScreen();
    case 'report':
      return reportScreen(b);
    case 'cases':
      return steward ? casesScreen() : feedScreen();
    case 'case':
      return steward ? caseScreen(b) : feedScreen();
    case 'decide':
      return steward ? decideScreen(b) : feedScreen();
    case 'vouch':
      return vouchScreen();
    case 'rules':
      return rulesScreen();
    default:
      return feedScreen();
  }
}

function tabs(active: Tab) {
  const me = w.me;
  if (!isMember(w, me.id) || me.role === 'triage' || me.role === 'community') return html``;
  const items: { tab: Exclude<Tab, null>; href: string; label: string }[] = [
    { tab: 'nearby', href: '#/', label: t('tab.nearby') },
    { tab: 'sell', href: '#/sell', label: t('tab.sell') },
    { tab: 'chats', href: '#/chats', label: t('tab.chats') },
    ...(stewards(w).includes(me.id) ? [{ tab: 'cases' as const, href: '#/cases', label: t('tab.cases') }] : []),
    { tab: 'me', href: '#/me', label: t('tab.me') },
  ];
  return html`${items.map(
    (i) => html`<a href="${i.href}" ${i.tab === active ? html`aria-current="page"` : ''}>${icon(i.tab)}<span>${i.label}</span></a>`,
  )}`;
}

function seatbar() {
  const me = w.me;
  const skew = Math.round(w.skew / DAY);
  return html`<div class="seat-who">
      <span class="seat-label">Seat</span>
      <b>${me.name.en}</b>
      ${skew ? html`<span class="seat-clock">clock +${skew}d</span>` : ''}
    </div>
    <button type="button" class="seat-switch" data-act="seats" aria-haspopup="dialog" aria-keyshortcuts="S">Switch seat</button>`;
}

function seatsDialog() {
  const group = (title: string, keys: string[]) => html`<h3>${title}</h3><ul class="seats">
    ${keys.map((k) => {
      const p = w.p(k);
      return html`<li><button type="button" data-act="seat" data-key="${k}" ${k === w.seat ? html`aria-current="true"` : ''}>
        <b>${p.name.en}</b><span>${p.blurb.en}</span></button></li>`;
    })}
  </ul>`;
  const order = SEAT_ORDER.filter((k) => w.byKey.has(k));
  const residents = order.filter((k) => w.p(k).role === 'resident' || (w.p(k).role === 'newcomer' && isMember(w, w.p(k).id)));
  const roles = order.filter((k) => ['steward', 'triage', 'community'].includes(w.p(k).role));
  const outside = order.filter((k) => w.p(k).role === 'newcomer' && !isMember(w, w.p(k).id));
  return html`<form method="dialog" class="sheet seats-sheet">
    <h2 id="seats-title">Whose seat?</h2>
    <p class="muted">The same world, seen from each person’s phone. Each seat sees only what its audience allows.</p>
    ${group('Residents', residents)}
    ${group('People with a role', roles)}
    ${outside.length ? group('Not members yet', outside) : ''}
    <h3>Clock</h3>
    <p class="muted">Now ${fmtDateTime(w.now(), 'en')}. Pauses end on their own dates.</p>
    <div class="row">
      <button type="button" class="quiet" data-act="advance" data-days="1">Move 1 day ahead</button>
      <button type="button" class="quiet" data-act="advance" data-days="3">Move 3 days ahead</button>
      <button type="button" class="quiet" data-act="advance" data-days="8">Move 8 days ahead</button>
    </div>
    ${languagePicker()}
    <div class="row">
      <button type="button" class="quiet" data-act="reset">Start the neighbourhood again</button>
      <button class="btn" value="close">Done</button>
    </div>
  </form>`;
}

let lastKey = '';
let queued = false;
let busy = 0; // while an action runs, intermediate world changes do not repaint half-finished screens

export function render(): void {
  const s = route();
  const l = local();
  const here = `${w.seat}|${location.hash}`;
  if (l.shownOn && l.shownOn !== here) {
    l.flash = undefined;
    l.why = undefined;
    l.shownOn = undefined;
  }
  if ((l.flash || l.why) && !l.shownOn) l.shownOn = here;
  const why = l.why;
  const screen = $('screen');
  const pre =
    (l.flash ? html`<p class="flash" role="status">${l.flash}</p>`.value : '') +
    (why ? whyCard(explain(w, why.predicate, why.decision, w.me.id), why.title).value : '');
  screen.innerHTML = pre + s.body.value;
  if (!s.body.value.includes('class="error"')) l.error = undefined;
  document.title = s.title ? `${s.title} · ${t('app.name')}` : t('app.name');
  $('tabs').innerHTML = tabs(s.tab).value;
  $('tabs').hidden = !$('tabs').innerHTML.trim();
  $('seatbar').innerHTML = seatbar().value;
  const seats = $<HTMLDialogElement>('seats');
  if (seats.open) seats.innerHTML = seatsDialog().value;
  if (desktop.matches) renderRecord($('record'));
  if (here !== lastKey) {
    lastKey = here;
    $('app-pane').scrollTop = 0;
    window.scrollTo(0, 0);
    if (!why) screen.querySelector<HTMLElement>('h1')?.focus({ preventScroll: true });
  }
}

const schedule = (): void => {
  if (queued || busy) return;
  queued = true;
  queueMicrotask(() => {
    queued = false;
    render();
  });
};

function speak(text: string): void {
  speechSynthesis.cancel();
  const u = new SpeechSynthesisUtterance(text);
  u.lang = w.language === 'hi' ? 'hi-IN' : 'en-IN';
  u.rate = 0.95;
  speechSynthesis.speak(u);
}

function openSeats(): void {
  const d = $<HTMLDialogElement>('seats');
  d.innerHTML = seatsDialog().value;
  d.showModal();
}

function setSeat(key: string): void {
  w.setSeat(key);
  const d = $<HTMLDialogElement>('seats');
  if (d.open) d.close();
}

export async function start(): Promise<void> {
  const fresh = new URLSearchParams(location.search).has('fresh');
  await w.boot(seed, { fresh });

  w.addEventListener('change', schedule);
  w.addEventListener('checks', () => desktop.matches && renderRecord($('record')));
  window.addEventListener('hashchange', render);
  desktop.addEventListener('change', render);

  // Built-in actions that belong to the shell.
  acts.set('seats', openSeats);
  acts.set('seat', (el) => setSeat(el.dataset.key!));
  acts.set('advance', (el) => w.advance(Number(el.dataset.days) * DAY));
  acts.set('reset', async () => {
    resetLocals();
    $<HTMLDialogElement>('seats').close();
    await w.reset(seed);
    go('#/');
  });
  acts.set('listen', (el) => speak(el.dataset.text ?? ''));
  acts.set('jump', (el) => jump($('record'), el.dataset.id!));
  acts.set('audit', async () => {
    setAudit('running');
    renderRecord($('record'));
    setAudit(await w.audit());
    renderRecord($('record'));
  });
  acts.set('record-mode', (el) => {
    setRecordMode((el as HTMLInputElement).value === 'seat' ? 'seat' : 'all');
    renderRecord($('record'));
  });

  // Every action runs to completion before the screen repaints once.
  const run = async (fn: () => void | Promise<void>): Promise<void> => {
    busy += 1;
    const before = location.hash;
    try {
      await fn();
    } finally {
      busy -= 1;
      // A changed hash repaints through its own hashchange event; repainting here too would race it.
      if (!busy && location.hash === before) render();
    }
  };
  document.addEventListener('click', (e) => {
    const el = (e.target as HTMLElement).closest<HTMLElement>('[data-act]');
    if (!el || el instanceof HTMLInputElement) return;
    const h = acts.get(el.dataset.act!);
    if (h) {
      e.preventDefault();
      void run(() => h(el));
    }
  });
  document.addEventListener('change', (e) => {
    const el = e.target as HTMLElement;
    if (!(el instanceof HTMLInputElement) || !el.dataset.act) return;
    const h = acts.get(el.dataset.act);
    if (h) void run(() => h(el));
  });
  document.addEventListener('submit', (e) => {
    const form = e.target as HTMLFormElement;
    const name = form.dataset.form;
    if (!name) return;
    e.preventDefault();
    const submitter = (e as SubmitEvent).submitter as HTMLButtonElement | null;
    const h = forms.get(name);
    if (h) void run(() => h(new FormData(form, submitter), form));
  });
  document.addEventListener('keydown', (e) => {
    const typing = (e.target as HTMLElement).closest('input, textarea, select, [contenteditable]');
    if (typing || e.metaKey || e.ctrlKey || e.altKey || $<HTMLDialogElement>('seats').open) return;
    const order = SEAT_ORDER.filter((k) => w.byKey.has(k));
    const i = order.indexOf(w.seat);
    if (e.key === 's' || e.key === 'S') {
      e.preventDefault();
      openSeats();
    } else if (e.key === ']') setSeat(order[(i + 1) % order.length]!);
    else if (e.key === '[') setSeat(order[(i - 1 + order.length) % order.length]!);
  });

  render();
  document.body.dataset.ready = 'true';
}
